import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { clientDetection } from '../globalFunctions/globalFunctions';
import { IRegistryApiService } from '../interfaces/registry-api.interface';
import { AttendeePreview } from '../models/attendee-preview.model';
import { AttendeeType } from '../models/attendee-type.model';
import { Attendee } from '../models/attendee.model';
import { Event } from '../models/event.model';
import { IdentityUserContext } from '../models/identity-user-context.model';
import { IdentityUser } from '../models/identityuser.model';
import { PageResult } from '../models/page-result.model';

@Injectable({ providedIn: 'root' })
export class RegistryApiService implements IRegistryApiService {
    private httpHeaders;

    private apiRoute = 'trainingproviderevents';

    private hasGottenSmartLabels = false;

    private smartLabels: Map<string, string> = new Map<string, string>();

    private hasGottenAppSettings = false;

    private appSettings: Map<string, string> = new Map<string, string>();

    constructor(private readonly httpClient: HttpClient) {
        this.httpHeaders = new HttpHeaders()
            .set('Accept-Language', 'en-US,en;q=0.9')
            .set('Cache-Control', 'no-cache')
            .set('Pragma', 'no-cache');
    }

    setParams(params: { [key: string]: unknown }): HttpParams {
        let httpParams = new HttpParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value) {
                httpParams = httpParams.set(key, value.toString());
            }
        });

        return httpParams;
    }

    private getUrl(endpoint: string): string {
        return `${environment.API_URL[clientDetection()]}/${endpoint}`;
    }

    async getEvent(eventId: number): Promise<Event> {
        const url: string = this.getUrl(`${this.apiRoute}/${eventId}`);
        return firstValueFrom(this.httpClient.get<Event>(url, { headers: this.httpHeaders }));
    }

    async getEvents(params?: {
        pageNumber?: number;
        pageSize?: number;
        courseName?: string;
        eventId?: number;
        showFutureEvents?: boolean;
    }): Promise<PageResult<Event>> {
        const url: string = this.getUrl(`${this.apiRoute}/`);
        let httpParams = new HttpParams();
        if (params) {
            httpParams = this.setParams(params);
        }
        return firstValueFrom(
            this.httpClient.get<PageResult<Event>>(url, { params: httpParams, headers: this.httpHeaders }),
        );
    }

    async getEventRoster(
        eventId: number,
        params?: {
            pageNumber?: number;
            pageSize?: number;
            daysShowPast?: number;
        },
    ): Promise<PageResult<Attendee>> {
        let httpParams = new HttpParams();
        if (params) {
            httpParams = this.setParams(params);
        }
        const url: string = this.getUrl(`${this.apiRoute}/${eventId}/roster`);
        return firstValueFrom(
            this.httpClient.get<PageResult<Attendee>>(url, { params: httpParams, headers: this.httpHeaders }),
        );
    }

    async getAttendeeTypes(): Promise<AttendeeType[]> {
        const url: string = this.getUrl(`${this.apiRoute}/attendeetypes?activeOnly=true`);
        return firstValueFrom(this.httpClient.get<AttendeeType[]>(url, { headers: this.httpHeaders }));
    }

    async getAttendeePreview(eventId: number, attendeeId: number): Promise<AttendeePreview> {
        const url: string = this.getUrl(`${this.apiRoute}/${eventId}/roster/newattendee/preview`);
        const httpParams = new HttpParams().set('appId', attendeeId.toString());
        return firstValueFrom(
            this.httpClient.get<AttendeePreview>(url, { params: httpParams, headers: this.httpHeaders }),
        );
    }

    async addAttendee(
        eventId: number,
        appId: number,
        attendeeTypeId?: number | undefined,
        pageNumber?: number | undefined,
        pageSize?: number | undefined,
    ): Promise<PageResult<Attendee>> {
        const url: string = this.getUrl(`${this.apiRoute}/${eventId}/roster/add`);
        const httpParams = this.setParams({
            appId: appId,
            pageNumber: pageNumber,
            pageSize: pageSize,
            attendeeTypeId: attendeeTypeId,
        });
        return firstValueFrom(
            this.httpClient.post<Promise<PageResult<Attendee>>>(url, null, {
                params: httpParams,
                headers: this.httpHeaders,
            }),
        );
    }

    async getApplicant(applicantId: number): Promise<Attendee> {
        if (applicantId == this.getAppID()) {
            const url: string = this.getUrl(`apps/${applicantId}`);
            return firstValueFrom(this.httpClient.get<Attendee>(url, { headers: this.httpHeaders }));
        } else {
            const url: string = this.getUrl(`IndividualContactInfo/${applicantId}`);
            return firstValueFrom(this.httpClient.get<Attendee>(url, { headers: this.httpHeaders }));
        }
    }

    async verifyAttendee(attendeeId: number, eventId: number): Promise<boolean> {
        let httpParams = new HttpParams();
        httpParams = httpParams.set('attendeeId', `${attendeeId}`);
        const url: string = this.getUrl(`${this.apiRoute}/${eventId}/verifyattendee`);
        return firstValueFrom(
            this.httpClient.post<boolean>(url, null, { headers: this.httpHeaders, params: httpParams }),
        );
    }

    async isValidQRCode(eventId: number, qrCode: string): Promise<AttendeePreview> {
        let httpParams = new HttpParams();
        httpParams = httpParams.set('qrCode', `${qrCode}`);
        const url: string = this.getUrl(`${this.apiRoute}/${eventId}/isvalidqrcode`);
        return firstValueFrom(
            this.httpClient.get<AttendeePreview>(url, { headers: this.httpHeaders, params: httpParams }),
        );
    }

    async getIdentityUserContexts(): Promise<IdentityUserContext[]> {
        const url: string = this.getUrl('identityusercontexts');
        return firstValueFrom(this.httpClient.get<IdentityUserContext[]>(url, { headers: this.httpHeaders }));
    }

    async getIdentityUser(): Promise<IdentityUser> {
        const url: string = this.getUrl('IdentityUsers/');
        return firstValueFrom(this.httpClient.get<IdentityUser>(url, { headers: this.httpHeaders }));
    }

    async getAllAppSettings(): Promise<void> {
        const url: string = this.getUrl(`${this.apiRoute}/getallappsettings`);
        return firstValueFrom(this.httpClient.get<Record<string, string>>(url, { headers: this.httpHeaders })).then(
            (response) => {
                this.appSettings = new Map(Object.entries(response).map(([key, value]) => [key, value]));
                this.hasGottenAppSettings = true;
            },
        );
    }

    async getAppSetting(key: string): Promise<string> {
        if (!this.hasGottenAppSettings) {
            await this.getAllAppSettings();
        }
        return this.appSettings.get(key) === undefined ? 'N/A' : this.appSettings.get(key)!;
    }

    async doesUserHaveTrainerLevelIDAccess(): Promise<boolean> {
        const url: string = this.getUrl(`${this.apiRoute}/doesuserhavetrainerlevelidaccess`);
        return firstValueFrom(this.httpClient.get<boolean>(url, { headers: this.httpHeaders }));
    }

    //These methods are not implemented but were used in the old Xamarin app, they probably can be deprecated

    getAppID(): number {
        return 0;
    }

    isLoggedInUserAnOrganization(): void {
        /** TODO: Nothing is happening in this method as of now. */
    }

    storeIdentityUserContext(): IdentityUserContext[] {
        return [];
    }
}
